import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  TextField,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Modal,
  Paper,
  InputAdornment,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Fade,
  CircularProgress,
  Stack,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  MusicNote as MusicIcon,
  Piano as PianoIcon,
  GraphicEq as EqualizerIcon,
  Close as CloseIcon,
  Launch as LaunchIcon,
  Headphones as HeadphonesIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Person as PersonIcon,
  LocationOn as LocationOnIcon,
  LocalOffer as LocalOfferIcon,
  Album as AlbumIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

// 랜드로버 디펜더 스타일 테마 - 바디 그린 베이스
const modernTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#254642', // 바디 그린 (메인)
      light: '#2d5a54',
      dark: '#17221F', // 딥 다크 (강조)
    },
    secondary: {
      main: '#4F5741', // 루프 그레이 (세컨더리)
      light: '#6a7356',
      dark: '#3a4030',
    },
    background: {
      default: '#f5f5f0', // 연한 베이지 배경
      paper: '#ffffff',
    },
    text: {
      primary: '#17221F', // 딥 다크 (텍스트)
      secondary: '#4D5452', // 실버 휠 (보조 텍스트)
    },
    success: {
      main: '#254642',
      light: '#2d5a54',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
    },
  },
  typography: {
    fontFamily: '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.025em',
    },
    h5: {
      fontWeight: 700,
      letterSpacing: '-0.025em',
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      lineHeight: 1.7,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          border: 'none',
          backgroundImage: 'none',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '16px',
          textTransform: 'none',
          fontWeight: 700,
          padding: '12px 32px',
          boxShadow: 'none',
          background: 'linear-gradient(135deg, #254642 0%, #3d7a6f 100%)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
            transition: 'left 0.5s ease',
          },
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(37, 70, 66, 0.5), 0 0 20px rgba(61, 122, 111, 0.3)',
            '&::before': {
              left: '100%',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '9999px',
            backgroundColor: '#f9fafb',
            '& fieldset': {
              borderColor: '#e5e7eb',
            },
            '&:hover fieldset': {
              borderColor: '#254642',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#254642',
              borderWidth: '2px',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          fontWeight: 700,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: '9999px',
          backgroundColor: '#f9fafb',
          '& fieldset': {
            borderColor: '#e5e7eb',
          },
          '&:hover fieldset': {
            borderColor: '#254642',
          },
          '&.Mui-focused fieldset': {
            borderColor: '#254642',
            borderWidth: '2px',
          },
        },
      },
    },
  },
});

function App() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('crawled_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [source, setSource] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // 무한 스크롤을 위한 옵저버 설정
  const observerRef = React.useRef();
  const lastItemRef = useCallback(node => {
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => prevPage + 1);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore]);

  // 데이터 로드 함수
  const loadItems = useCallback(async (pageNum = 1, reset = false) => {
    if (loading) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '12',
        ...(searchTerm && { search: searchTerm }),
        ...(category && { category }),
        ...(source && { source }),
        sortBy: sortBy,
        sortOrder: sortOrder,
      });

      const response = await axios.get(`${API_BASE_URL}/items?${params}`);
      const newItems = response.data.items || [];

      if (reset || pageNum === 1) {
        setItems(newItems);
      } else {
        setItems(prev => [...prev, ...newItems]);
      }

      setHasMore(newItems.length === 12);
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, category, source, sortBy, sortOrder, loading]);

  // 검색 및 필터 변경 시 리셋
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPage(1);
    setHasMore(true);
  };

  const handleFilterChange = (filterType, value) => {
    switch (filterType) {
      case 'category':
        setCategory(value);
        break;
      case 'source':
        setSource(value);
        break;
      case 'sortBy':
        setSortBy(value);
        break;
      case 'sortOrder':
        setSortOrder(value);
        break;
      default:
        break;
    }
    setPage(1);
    setHasMore(true);
  };


  // 초기 로드
  useEffect(() => {
    loadItems(1, true);
  }, []);

  // 페이지 변경 시 로드
  useEffect(() => {
    if (page > 1) {
      loadItems(page);
    }
  }, [page]);

  // 필터 변경 시 리셋하여 로드
  useEffect(() => {
    loadItems(1, true);
  }, [searchTerm, category, source, sortBy, sortOrder]);

  // 유틸리티 함수들
  const formatPrice = (price) => {
    if (!price) return '가격 문의';

    // 이미 포맷된 문자열인 경우
    if (typeof price === 'string' && (price.includes('만원') || price.includes('원'))) {
      return price;
    }

    // 숫자로 변환 시도
    const numPrice = typeof price === 'string'
      ? parseInt(price.replace(/[^0-9]/g, ''))
      : price;

    if (isNaN(numPrice)) return '가격 문의';

    // 만원 단위로 변환
    if (numPrice >= 10000) {
      const manwon = Math.floor(numPrice / 10000);
      const remainder = numPrice % 10000;
      if (remainder === 0) {
        return `${manwon.toLocaleString()}만원`;
      } else {
        return `${manwon.toLocaleString()}.${Math.floor(remainder / 1000)}만원`;
      }
    }

    return `${numPrice.toLocaleString()}원`;
  };

  // 날짜 포맷 함수 추가
  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInMinutes = Math.floor((now - date) / (1000 * 60));

      if (diffInMinutes < 1) {
        return '방금 전';
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}분 전`;
      } else if (diffInMinutes < 1440) { // 24시간
        const hours = Math.floor(diffInMinutes / 60);
        return `${hours}시간 전`;
      } else if (diffInMinutes < 10080) { // 7일
        const days = Math.floor(diffInMinutes / 1440);
        return `${days}일 전`;
      } else {
        // 7일 이상이면 날짜 표시
        const month = date.getMonth() + 1;
        const day = date.getDate();
        return `${month}월 ${day}일`;
      }
    } catch (error) {
      return '';
    }
  };

  const handleItemClick = (item) => {
    // API에서 받은 상세 정보를 detail 객체로 구조화
    const itemWithDetail = {
      ...item,
      detail: {
        description: item.description,
        sellerInfo: item.seller_info,
        location: item.location,
        condition: item.condition,
        brand: item.brand,
        model: item.model,
        year: item.year,
        contactInfo: item.contact_info,
        tags: item.tags || [],
        status: item.status,
        shipping: item.shipping,
        warranty: item.warranty,
        accessories: item.accessories,
        images: item.images || [],
        crawledAt: item.crawled_at,
        sourceUrl: item.link
      }
    };
    setSelectedItem(itemWithDetail);
    setDetailModalOpen(true);
  };

  // Placeholder 이미지 (SVG data URI)
  const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="300" height="220"%3E%3Crect width="300" height="220" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%23999"%3E이미지 없음%3C/text%3E%3C/svg%3E';

  const getImageUrl = (imageUrl, itemId, itemLink) => {
    // 이미지 URL이 없으면 placeholder
    if (!imageUrl) {
      return PLACEHOLDER_IMAGE;
    }

    // 뮬 CDN 이미지는 백엔드 프록시를 통해 로드 (Cloudflare hotlink protection 우회)
    if (imageUrl.includes('cdn.mule.co.kr')) {
      const proxyUrl = `${API_BASE_URL}/proxy-image?url=${encodeURIComponent(imageUrl)}`;
      return proxyUrl;
    }

    // 다른 HTTP 이미지는 직접 접근
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }

    // 상대 경로는 API 베이스 URL 추가
    const fullUrl = `${API_BASE_URL}${imageUrl}`;
    return fullUrl;
  };

  const handleImageError = (e) => {
    // 이미 placeholder면 무한 루프 방지
    if (e.target.src === PLACEHOLDER_IMAGE) {
      return;
    }
    console.error('Image load error for URL:', e.target.src);
    e.target.src = PLACEHOLDER_IMAGE;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case '일렉기타':
      case '어쿠스틱기타':
        return <MusicIcon />;
      case '키보드':
      case '피아노':
        return <PianoIcon />;
      case '앰프':
      case '이펙터':
        return <EqualizerIcon />;
      case '드럼':
        return <AlbumIcon />;
      default:
        return <HeadphonesIcon />;
    }
  };

  return (
    <ThemeProvider theme={modernTheme}>
      <CssBaseline />
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #e8e8e0 0%, #f5f5f0 50%, #deded5 100%)',
      }}>
        {/* 헤더 */}
        <AppBar position="sticky" elevation={0} sx={{
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: 'none',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        }}>
          <Toolbar sx={{ py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #254642, #3d7a6f)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
              }}>
                <span style={{ fontSize: '18px' }}>🎵</span>
              </Box>
              <Typography variant="h6" component="div" sx={{
                fontWeight: 700,
                color: '#17221F',
                letterSpacing: '-0.5px',
              }}>
                악기장터
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* 검색 및 필터 섹션 */}
          <Fade in timeout={1000}>
            <Paper sx={{
              p: 4,
              mb: 4,
              background: '#ffffff',
              borderRadius: '24px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            }}>
              <Typography variant="h6" gutterBottom sx={{
                mb: 3,
                color: '#17221F',
                fontWeight: 700,
              }}>
                악기를 찾아보세요
              </Typography>

              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    label="🔍 악기 검색"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="기타, 피아노, 드럼..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#254642' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>🎼 카테고리</InputLabel>
                    <Select
                      value={category}
                      onChange={(e) => handleFilterChange('category', e.target.value)}
                      label="🎼 카테고리"
                    >
                      <MenuItem value="">전체</MenuItem>
                      <MenuItem value="일렉기타">🎸 일렉기타</MenuItem>
                      <MenuItem value="어쿠스틱기타">🎵 어쿠스틱기타</MenuItem>
                      <MenuItem value="베이스">🎶 베이스</MenuItem>
                      <MenuItem value="드럼">🥁 드럼</MenuItem>
                      <MenuItem value="키보드">🎹 키보드</MenuItem>
                      <MenuItem value="앰프">🔊 앰프</MenuItem>
                      <MenuItem value="이펙터">⚡ 이펙터</MenuItem>
                      <MenuItem value="기타">🎤 기타</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>📍 출처</InputLabel>
                    <Select
                      value={source}
                      onChange={(e) => handleFilterChange('source', e.target.value)}
                      label="📍 출처"
                    >
                      <MenuItem value="">전체</MenuItem>
                      <MenuItem value="mule">🎸 뮬</MenuItem>
                      <MenuItem value="joongna">🛒 중고나라</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>📊 정렬</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                      label="📊 정렬"
                    >
                      <MenuItem value="crawled_at">⏰ 최신순</MenuItem>
                      <MenuItem value="title">📝 제목순</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>🔄 순서</InputLabel>
                    <Select
                      value={sortOrder}
                      onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                      label="🔄 순서"
                    >
                      <MenuItem value="desc">⬇️ 내림차순</MenuItem>
                      <MenuItem value="asc">⬆️ 오름차순</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

            </Paper>
          </Fade>

          {/* 상품 목록 - Fixed Width Cards */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, 280px)',
            gap: 3,
            justifyContent: 'center',
          }}>
            {items.map((item, index) => (
              <Box
                key={`${item.id}-${index}`}
                ref={index === items.length - 1 ? lastItemRef : null}
              >
                <Fade in timeout={300}>
                  <Card
                    sx={{
                      height: 420,
                      display: 'flex',
                      flexDirection: 'column',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                    onClick={() => handleItemClick(item)}
                  >
                    <Box sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: '16px 16px 0 0',
                      height: 200,
                      flexShrink: 0,
                    }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={getImageUrl(item.image_url, item.id, item.link)}
                        alt={item.title}
                        onError={handleImageError}
                        onLoad={() => console.log('Image loaded successfully:', item.title, getImageUrl(item.image_url, item.id, item.link))}
                        data-original-url={item.image_url}
                        sx={{
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.05)',
                          },
                        }}
                      />
                      <Box sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        display: 'flex',
                        gap: 1,
                        zIndex: 2,
                      }}>
                        <Chip
                          label={item.category || '기타'}
                          size="small"
                          sx={{
                            background: '#4F5741', // 루프 그레이
                            color: 'white',
                            fontWeight: 600,
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                          }}
                        />
                      </Box>
                      <Box sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: 16,
                        zIndex: 2,
                      }}>
                        <Chip
                          label={item.source === 'mule' ? '🎸 뮬' : '🛒 중고나라'}
                          size="small"
                          sx={{
                            background: item.source === 'mule'
                              ? '#254642' // 바디 그린
                              : '#4D5452', // 실버 휠
                            color: 'white',
                            fontWeight: 600,
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                          }}
                        />
                      </Box>
                    </Box>

                    <CardContent sx={{
                      flexGrow: 1,
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      height: 220,
                    }}>
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 'bold',
                          mb: 1.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          color: '#17221F', // 딥 다크
                          lineHeight: 1.3,
                          minHeight: 50,
                        }}
                      >
                        {item.title}
                      </Typography>

                      <Typography
                        variant="h6"
                        sx={{
                          color: '#254642',
                          fontWeight: 'bold',
                          fontSize: '1.2rem',
                          mb: 1.5,
                        }}
                      >
                        {formatPrice(item.price)}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          lineHeight: 1.4,
                          mb: 2,
                          color: '#4D5452', // 실버 휠
                          fontSize: '0.875rem',
                          flexGrow: 1,
                        }}
                      >
                        {item.description || ''}
                      </Typography>

                      {/* 날짜 정보 추가 */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mt: 'auto',
                        pt: 1,
                      }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#9ca3af',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          ⏰ {formatDate(item.post_date || item.created_at)}
                        </Typography>

                        {item.views > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: '#9ca3af',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                            }}
                          >
                            👁️ {item.views}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Fade>
              </Box>
            ))}
          </Box>

          {/* 로딩 인디케이터 */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress
                  size={50}
                  sx={{
                    color: '#254642',
                    mb: 2,
                  }}
                />
                <Typography variant="h6" sx={{
                  color: '#17221F',
                  fontWeight: 700,
                }}>
                  🎶 더 많은 악기를 불러오는 중...
                </Typography>
              </Box>
            </Box>
          )}

          {/* 더 이상 데이터가 없을 때 */}
          {!hasMore && items.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Paper sx={{
                p: 4,
                textAlign: 'center',
                background: '#ffffff',
                borderRadius: '20px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}>
                <MusicIcon sx={{ fontSize: 60, color: '#254642', mb: 2 }} />
                <Typography variant="h6" sx={{
                  color: '#17221F',
                  fontWeight: 700,
                }}>
                  🎵 모든 악기를 확인했습니다!
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, color: '#4D5452' }}>
                  새로운 음악의 여정을 시작해보세요
                </Typography>
              </Paper>
            </Box>
          )}

          {/* 검색 결과가 없을 때 */}
          {!loading && items.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Paper sx={{
                p: 6,
                textAlign: 'center',
                background: '#ffffff',
                borderRadius: '20px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}>
                <HeadphonesIcon sx={{ fontSize: 80, color: '#254642', mb: 3 }} />
                <Typography variant="h4" sx={{
                  color: '#17221F',
                  fontWeight: 700,
                  mb: 2,
                }}>
                  🎼 원하는 악기를 찾지 못했나요?
                </Typography>
                <Typography variant="body1" sx={{ color: '#4D5452' }}>
                  다른 검색어나 필터를 시도해보세요
                </Typography>
              </Paper>
            </Box>
          )}
        </Container>

        {/* 상세 모달 */}
        <Modal
          open={detailModalOpen}
          onClose={() => setDetailModalOpen(false)}
          closeAfterTransition
          sx={{
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <Fade in={detailModalOpen}>
            <Paper
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90%',
                maxWidth: 1000,
                maxHeight: '90vh',
                overflow: 'auto',
                p: 0,
                background: '#ffffff',
                borderRadius: '24px',
                boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f3f4f6',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#d1d5db',
                  borderRadius: '4px',
                  '&:hover': {
                    background: '#9ca3af',
                  },
                },
              }}
            >
              {selectedItem && (
                <Box>
                  {/* 모달 헤더 */}
                  <Box sx={{
                    position: 'relative',
                    background: '#f9fafb',
                    p: 4,
                    borderRadius: '24px 24px 0 0',
                    borderBottom: '1px solid #e5e7eb',
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Typography variant="h4" component="h2" sx={{
                        flexGrow: 1,
                        fontWeight: 700,
                        color: '#17221F',
                        pr: 2,
                      }}>
                        {selectedItem.title}
                      </Typography>
                      <IconButton
                        onClick={() => setDetailModalOpen(false)}
                        sx={{
                          color: '#4D5452',
                          '&:hover': {
                            background: '#e5e7eb',
                            transform: 'rotate(90deg)',
                          },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box sx={{ p: 4 }}>
                    <Grid container spacing={4}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{
                          position: 'relative',
                          borderRadius: '16px',
                          overflow: 'hidden',
                          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                        }}>
                          <img
                            src={getImageUrl(selectedItem.image_url, selectedItem.id)}
                            alt={selectedItem.title}
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '500px',
                              objectFit: 'contain',
                            }}
                            onError={handleImageError}
                          />
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{
                          p: 4,
                          background: '#f9fafb',
                          borderRadius: '16px',
                          mb: 3,
                          border: '1px solid #e5e7eb',
                        }}>
                          <Typography variant="h3" sx={{
                            fontWeight: 700,
                            color: '#254642',
                            textAlign: 'center',
                          }}>
                            💰 {formatPrice(selectedItem.price)}
                          </Typography>
                        </Box>

                        <Stack spacing={3}>
                          <Box sx={{
                            p: 3,
                            background: '#ffffff',
                            borderRadius: '16px',
                            border: '1px solid #e5e7eb',
                            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                          }}>
                            <Typography variant="h6" sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: '#17221F',
                              fontWeight: 700,
                              mb: 2,
                            }}>
                              {getCategoryIcon(selectedItem.category)}
                              <Box component="span" sx={{ ml: 1 }}>상품 정보</Box>
                            </Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="body1" color="text.secondary">카테고리</Typography>
                                <Typography variant="body1" fontWeight={600}>{selectedItem.category || '미분류'}</Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body1" color="text.secondary">출처</Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {selectedItem.source === 'mule' ? '🎸 뮬' : '🛒 중고나라'}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body1" color="text.secondary">등록일</Typography>
                                <Typography variant="body1" fontWeight={600}>
                                  {formatDate(selectedItem.created_at)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>

                          {selectedItem.description && (
                            <Box sx={{
                              p: 3,
                              background: 'rgba(37, 70, 66, 0.08)',
                              backdropFilter: 'blur(20px)',
                              borderRadius: '20px',
                              border: '1px solid rgba(37, 70, 66, 0.3)',
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                border: '1px solid rgba(37, 70, 66, 0.6)',
                                boxShadow: '0 8px 30px rgba(37, 70, 66, 0.2)',
                                transform: 'translateY(-2px)',
                              },
                            }}>
                              <Typography variant="h6" sx={{
                                background: 'linear-gradient(135deg, #254642, #3d7a6f)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontWeight: 700,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                              }}>
                                <MusicIcon sx={{ mr: 1, color: '#254642' }} />
                                상세 설명
                              </Typography>
                              <Typography variant="body1" sx={{
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.6,
                                maxHeight: '200px',
                                overflow: 'auto',
                              }}>
                                {selectedItem.description}
                              </Typography>
                            </Box>
                          )}

                          {/* 추가 이미지 갤러리 */}
                          {selectedItem.detail && selectedItem.detail.images && selectedItem.detail.images.length > 1 && (
                            <Box sx={{
                              p: 3,
                              background: '#0f172a',
                              borderRadius: '12px',
                              border: '1px solid #334155',
                            }}>
                              <Typography variant="h6" sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                              }}>
                                <PhotoLibraryIcon sx={{ mr: 1 }} />
                                추가 이미지 ({selectedItem.detail.images.length}장)
                              </Typography>
                              <Box sx={{
                                display: 'flex',
                                gap: 2,
                                overflowX: 'auto',
                                pb: 1,
                              }}>
                                {selectedItem.detail.images.slice(1, 6).map((imageUrl, index) => (
                                  <Box
                                    key={index}
                                    sx={{
                                      minWidth: 120,
                                      height: 120,
                                      borderRadius: '8px',
                                      overflow: 'hidden',
                                      border: '2px solid #334155',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                      '&:hover': {
                                        transform: 'scale(1.05)',
                                        borderColor: 'primary.main',
                                      },
                                    }}
                                    onClick={() => window.open(getImageUrl(imageUrl, selectedItem.id), '_blank')}
                                  >
                                    <img
                                      src={getImageUrl(imageUrl, selectedItem.id)}
                                      alt={`추가 이미지 ${index + 1}`}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                      }}
                                      onError={handleImageError}
                                    />
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}

                          {/* 판매자 정보 */}
                          {selectedItem.detail && selectedItem.detail.sellerInfo && (
                            <Box sx={{
                              p: 3,
                              background: '#0f172a',
                              borderRadius: '12px',
                              border: '1px solid #334155',
                            }}>
                              <Typography variant="h6" sx={{
                                color: 'secondary.main',
                                fontWeight: 600,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                              }}>
                                <PersonIcon sx={{ mr: 1 }} />
                                판매자 정보
                              </Typography>
                              <Typography variant="body1" sx={{
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.6,
                              }}>
                                {selectedItem.detail.sellerInfo}
                              </Typography>
                            </Box>
                          )}

                          {/* 위치 정보 */}
                          {selectedItem.detail && selectedItem.detail.location && (
                            <Box sx={{
                              p: 3,
                              background: '#0f172a',
                              borderRadius: '12px',
                              border: '1px solid #334155',
                            }}>
                              <Typography variant="h6" sx={{
                                color: 'secondary.main',
                                fontWeight: 600,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                              }}>
                                <LocationOnIcon sx={{ mr: 1 }} />
                                위치 정보
                              </Typography>
                              <Typography variant="body1" sx={{
                                lineHeight: 1.6,
                              }}>
                                {selectedItem.detail.location}
                              </Typography>
                            </Box>
                          )}

                          {/* 태그 정보 */}
                          {selectedItem.detail && selectedItem.detail.tags && selectedItem.detail.tags.length > 0 && (
                            <Box sx={{
                              p: 3,
                              background: '#0f172a',
                              borderRadius: '12px',
                              border: '1px solid #334155',
                            }}>
                              <Typography variant="h6" sx={{
                                color: 'secondary.main',
                                fontWeight: 600,
                                mb: 2,
                                display: 'flex',
                                alignItems: 'center',
                              }}>
                                <LocalOfferIcon sx={{ mr: 1 }} />
                                태그
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedItem.detail.tags.map((tag, index) => (
                                  <Chip
                                    key={index}
                                    label={tag}
                                    size="small"
                                    color="primary"
                                    sx={{
                                      fontWeight: 600,
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          )}

                          {selectedItem.link && (
                            <Button
                              variant="contained"
                              size="large"
                              fullWidth
                              onClick={() => window.open(selectedItem.link, '_blank')}
                              startIcon={<LaunchIcon />}
                              sx={{
                                py: 2.5,
                                fontSize: '1.2rem',
                                fontWeight: 800,
                                background: 'linear-gradient(135deg, #254642 0%, #3d7a6f 100%)',
                                borderRadius: '20px',
                                border: '2px solid rgba(37, 70, 66, 0.5)',
                                boxShadow: '0 8px 32px rgba(37, 70, 66, 0.4), inset 0 0 20px rgba(255, 255, 255, 0.1)',
                                position: 'relative',
                                overflow: 'hidden',
                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: '-100%',
                                  width: '100%',
                                  height: '100%',
                                  background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent)',
                                  transition: 'left 0.6s ease',
                                },
                                '&:hover': {
                                  transform: 'translateY(-4px) scale(1.02)',
                                  boxShadow: '0 16px 48px rgba(37, 70, 66, 0.6), 0 0 30px rgba(61, 122, 111, 0.4)',
                                  border: '2px solid rgba(61, 122, 111, 0.8)',
                                  '&::before': {
                                    left: '100%',
                                  },
                                },
                              }}
                            >
                              🎵 원본 사이트에서 보기
                            </Button>
                          )}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}
            </Paper>
          </Fade>
        </Modal>
      </Box>
    </ThemeProvider>
  );
}

export default App;
